import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bankPrefill, linkBankDoc, paymentNote } from '@/shared/utils/reverseChain';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { currencyRate, goodsReceipts, type GoodsReceiptItem, type GoodsReceiptService } from '@/api/goodsReceipts';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import { supplierOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { ProjectField } from '@/components/ProjectSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { fmtDocDate, moneyDocActions, vatTail } from '@/shared/utils/paymentFromDoc';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import QuickFixedAssetModal from '@/shared/components/QuickFixedAssetModal';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import { useMainContractAutoSelect } from '@/hooks/useContractDefaults';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';
import VatFlagsButton from '@/shared/components/VatFlagsButton';
import {
  DEFAULT_VAT_FLAGS, lineAmounts, orgVatDefaults, recalcRows, type DocVatFlags, vatPayerOn,
} from '@/shared/utils/vatFlags';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  supplier_order_id: string;
  supplier_doc_number: string;
  /** ч.1374: ознаки ПДВ документа як у 1С (умовчання — з картки організації). */
  vat_flags: DocVatFlags;
  advance_offset_mode: 'AUTO' | 'NONE';
  comment: string;
  project_id: string | null;
  items: GoodsReceiptItem[];
  services: GoodsReceiptService[];
}

// Типові рахунки витрат для послуг у приході (Дт — Кт 631; fallback 92).
const COST_ACCOUNTS: [string, string][] = [
  ['92', '92 — Адміністративні витрати'],
  ['91', '91 — Загальновиробничі'],
  ['93', '93 — Витрати на збут'],
  ['94', '94 — Інші операційні'],
  ['23', '23 — Виробництво'],
  ['152', '152 — Придбання ОЗ'],
  ['154', '154 — Придбання НМА'],
];

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  supplier_order_id: '',
  supplier_doc_number: '',
  vat_flags: { ...DEFAULT_VAT_FLAGS },
  advance_offset_mode: 'AUTO',
  comment: '',
  project_id: null,
  items: [],
  services: [],
};

const newRow = (
  lineNumber: number,
  defaultVatId: string,
  defaultTaxPurposeId: string | null = null,
): GoodsReceiptItem => ({
  line_number: lineNumber,
  nomenclature_id: '',
  quantity: '0',
  price: '0',
  amount_without_vat: '0',
  vat_rate_id: defaultVatId,
  vat_amount: '0',
  total_amount: '0',
  tax_purpose_id: defaultTaxPurposeId,
});

const fmt = (n: number) => n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ч.859: іконка документа для кнопок «створити …» (як у рахунку/реалізації). */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function GoodsReceiptEditor() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по purchases.receipt.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.receipt.create');
  const supplierList = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const costItemsQ = useQuery({ queryKey: ['cost-items'], queryFn: refs.costItems });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  // 2026-06-02: tax_purposes для класифікації ВК (ПКУ ст.199).
  // Default — TAXABLE («Господарська діяльність, оподатковувана ПДВ»).
  const taxPurposesList = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });
  const defaultTaxPurposeId = taxPurposesList.data?.find((tp) => tp.code === 'TAXABLE')?.id ?? null;
  const docQuery = useQuery({
    queryKey: ['goods-receipt', id],
    queryFn: () => goodsReceipts.get(id!),
    enabled: !isNew,
  });
  // ч.1267: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'goods-receipt', (cid) => goodsReceipts.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.goods_receipts', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  // 2026-06-02: Picker (catalog mode) для додавання товару.
  const [pickerOpen, setPickerOpen] = useState(false);
  // Курс валютного документа (2026-08-09): мігрований несе з 1С;
  // наш — тягнемо з НБУ на дату і зберігаємо у документ.
  const [fxRate, setFxRate] = useState<string>('');
  const [nbuOpen, setNbuOpen] = useState(false);
  const [nbuDate, setNbuDate] = useState('');
  const [nbuBusy, setNbuBusy] = useState(false);
  // 2026-07-31 (рішення власника): вкладки як у реалізації — Товари/Послуги
  // + Рахунки обліку / Податковий облік.
  const [linesTab, setLinesTab] = useState<'goods' | 'services' | 'equipment'>('goods');
  // 2026-07-31 (рішення власника, як в 1С): вкладка «Обладнання» —
  // рядок = звичайний item з рахунком 15х (капітальні інвестиції);
  // бухгалтер НЕ мусить памʼятати про перебивку рахунку.
  const isEquipRow = (it: GoodsReceiptItem) =>
    (it.account_code ?? '').startsWith('15');
  const [viewTab, setViewTab] = useState<'lines' | 'accounts' | 'tax'>('lines');
  // Превʼю РЕАЛЬНИХ рахунків запасів (той самий резолвер, що проведення) —
  // сірим у полях вкладки «Рахунки обліку» (як у реалізації).
  const previewAccountsQ = useQuery({
    queryKey: ['gr-preview-accounts', form.organization_id, form.warehouse_id,
      form.items.map((it) => it.nomenclature_id).join('|')],
    queryFn: () => goodsReceipts.previewAccounts({
      organization_id: form.organization_id,
      items: form.items.filter((it) => it.nomenclature_id).map((it) => ({
        nomenclature_id: it.nomenclature_id,
        warehouse_id: form.warehouse_id || null,
      })),
    }),
    enabled: viewTab === 'accounts' && !!form.organization_id
      && form.items.some((it) => it.nomenclature_id),
  });
  const previewByNom = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const r of previewAccountsQ.data ?? []) m.set(r.nomenclature_id, r.account_code);
    return m;
  }, [previewAccountsQ.data]);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  // 2026-07-31 (рішення власника): «створити з рахунку» — попап АКТИВНИХ
  // рахунків постачальника (лише PROFORMA_INVOICE) + єдина механіка
  // «Підстава» (✕ знімає звʼязок, поля блокуються).
  const [invoicePickerOpen, setInvoicePickerOpen] = useState(false);
  // Швидка картка ОЗ з рядка-обладнання (після проведення).
  const [quickFa, setQuickFa] = useState<{ name: string; cost: string } | null>(null);
  // Швидке створення номенклатури (як у рахунку).
  const [quickNomOpen, setQuickNomOpen] = useState(false);
  const supplierInvoicesQ = useQuery({
    queryKey: ['supplier-invoices-active', form.organization_id, form.contractor_id],
    queryFn: () => supplierOrders.list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id || undefined,
      order_kind: 'PROFORMA_INVOICE',
      closing_status: 'OPEN',
      posted: true,
      limit: 200,
    }),
    enabled: invoicePickerOpen && !!form.organization_id,
  });
  // Підстава (замовлення АБО рахунок): номер для показу + посилання.
  const basisOrderQ = useQuery({
    queryKey: ['supplier-order-brief', form.supplier_order_id],
    queryFn: () => supplierOrders.get(form.supplier_order_id),
    enabled: !!form.supplier_order_id,
  });

  // выбор default НДС 20%
  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);
  // 2026-06-02: ховаємо колонки ПДВ якщо org НЕ платник.
  // EXEMPT-rate автоматично проставляється у newRow → бухгалтер єдинника
  // без ПДВ взагалі не бачить колонок ПДВ/Призн. ПДВ/Сума ПДВ.
  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  // ч.1374: колонки ПДВ — платник І «враховувати ПДВ» у документі.
  // ч.1383: платник ПДВ — на дату документа; збережений документ з ПДВ лишається з ПДВ,
  // навіть якщо організація потім перестала бути платником.
  const docVatPayer = vatPayerOn(currentOrg, form.document_date)
    || (!isNew && form.vat_flags.vat_considered);
  const showVatColumns = docVatPayer && form.vat_flags.vat_considered;
  const rateOf = (vatRateId: string | null | undefined) =>
    vatList.data?.find((v) => v.id === vatRateId)?.rate_percent ?? 0;
  const vatFlagsTouched = useRef(false);
  useEffect(() => {
    if (!isNew || copy.source || vatFlagsTouched.current || !currentOrg) return;
    const next = orgVatDefaults(currentOrg, form.document_date);
    setForm((f) => (f.vat_flags.vat_considered === next.vat_considered
      && f.vat_flags.prices_include_vat === next.prices_include_vat && f.vat_flags.vat_auto === next.vat_auto)
      ? f : { ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf),
        services: recalcRows(f.services, next, rateOf) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, copy.source, currentOrg, vatList.data, form.document_date]);
  function applyVatFlags(next: DocVatFlags) {
    vatFlagsTouched.current = true;
    setForm((f) => ({ ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf),
      services: recalcRows(f.services, next, rateOf) }));
  }
  // Ознаки документа-підстави (рахунок постачальника) — ціни там уже з ПДВ або без.
  const orderVatFlags = (o: { vat_considered?: boolean; prices_include_vat?: boolean; vat_auto?: boolean }): DocVatFlags => ({
    vat_considered: o.vat_considered ?? true, prices_include_vat: o.prices_include_vat ?? false,
    vat_auto: o.vat_auto ?? true,
  });
  // Для неплатників — VAT_0 rate (буде в payload, але колонки сховані).
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveDefaultVatId = showVatColumns ? defaultVatId : exemptVatId;
  // Етап 2.1: карта nomenclature_id -> vat_rate_id (ставка ПДВ з картки).
  const vatRateByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.vat_rate_id) m.set(n.id, n.vat_rate_id);
    }
    return m;
  }, [nomList.data]);

  // ч.1423: податкове призначення з картки; немає на картці — типове «оподатковувані».
  const taxPurposeByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.tax_purpose_id) m.set(n.id, n.tax_purpose_id);
    }
    return m;
  }, [nomList.data]);

  // Підфіча #4 (2026-06-08): серії + характеристики у рядках приходу.
  // Карти nomenclature_id -> use_series / use_characteristics. Для рядків з
  // прапором — показуємо row detail з селектом серії / характеристики.
  const useSeriesMap = useMemo(() => {
    const m = new Map<string, boolean>();
    nomList.data?.items.forEach((n) => m.set(n.id, !!n.use_series));
    return m;
  }, [nomList.data]);
  const useCharMap = useMemo(() => {
    const m = new Map<string, boolean>();
    nomList.data?.items.forEach((n) => m.set(n.id, !!n.use_characteristics));
    return m;
  }, [nomList.data]);

  // Паралельні запити серій по номенклатурах рядків з use_series=true.
  const seriesNomIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of form.items) {
      if (it.nomenclature_id && useSeriesMap.get(it.nomenclature_id)) s.add(it.nomenclature_id);
    }
    return Array.from(s);
  }, [form.items, useSeriesMap]);
  const seriesQueries = useQueries({
    queries: seriesNomIds.map((nid) => ({
      queryKey: ['nomenclature-series', nid],
      queryFn: () => refs.nomenclatureSeries(nid),
      enabled: !!nid,
      staleTime: 60 * 1000,
    })),
  });
  const seriesByNom = useMemo(() => {
    const m: Record<string, { id: string; name: string; code: string | null }[]> = {};
    seriesNomIds.forEach((nid, idx) => {
      const q = seriesQueries[idx];
      if (q?.data) m[nid] = q.data;
    });
    return m;
  }, [seriesNomIds, seriesQueries]);

  // Паралельні запити характеристик по номенклатурах рядків з use_characteristics=true.
  const charNomIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of form.items) {
      if (it.nomenclature_id && useCharMap.get(it.nomenclature_id)) s.add(it.nomenclature_id);
    }
    return Array.from(s);
  }, [form.items, useCharMap]);
  const charQueries = useQueries({
    queries: charNomIds.map((nid) => ({
      queryKey: ['nomenclature-characteristics', nid],
      queryFn: () => refs.nomenclatureCharacteristics(nid),
      enabled: !!nid,
      staleTime: 60 * 1000,
    })),
  });
  const charByNom = useMemo(() => {
    const m: Record<string, { id: string; name: string; code: string | null }[]> = {};
    charNomIds.forEach((nid, idx) => {
      const q = charQueries[idx];
      if (q?.data) m[nid] = q.data;
    });
    return m;
  }, [charNomIds, charQueries]);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        warehouse_id: d.warehouse_id ?? '',
        supplier_order_id: d.supplier_order_id ?? '',
        supplier_doc_number: d.supplier_doc_number ?? '',
        vat_flags: {
          vat_considered: d.vat_considered ?? true,
          prices_include_vat: d.prices_include_vat,
          vat_auto: d.vat_auto ?? true,
        },
        advance_offset_mode: d.advance_offset_mode ?? 'AUTO',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          series_id: it.series_id ?? null,
          characteristic_id: it.characteristic_id ?? null,
          warehouse_id: it.warehouse_id ?? null,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          tax_purpose_id: it.tax_purpose_id ?? null,
          uom_id: it.uom_id ?? null,
        })),
        services: (d.services ?? []).map((sv) => ({
          line_number: sv.line_number,
          nomenclature_id: sv.nomenclature_id ?? null,
          description: sv.description ?? '',
          quantity: sv.quantity != null ? String(sv.quantity) : '1',
          price: sv.price != null ? String(sv.price) : '',
          amount_without_vat: String(sv.amount_without_vat),
          vat_rate_id: sv.vat_rate_id ?? null,
          vat_amount: String(sv.vat_amount),
          total_amount: String(sv.total_amount),
          cost_account_code: sv.cost_account_code ?? '92',
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка до замовлень і підстав
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), supplier_order_id: EMPTY_FORM.supplier_order_id, supplier_doc_number: EMPTY_FORM.supplier_doc_number, }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, defaultVatId, defaultTaxPurposeId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // ч.948 (рішення власника): «створити ↗» біля контрагента і договору
  // відкриває ПОПАП просто на сторінці документа (як у ПКО/РКО).
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);
  const contractorNameForQuick = (supplierList.data?.items ?? []).find((c: { id: string; name: string }) => c.id === form.contractor_id)?.name ?? '';
  useEffect(() => {
    const r = docQuery.data?.exchange_rate;
    if (r) setFxRate(String(r));
  }, [docQuery.data?.exchange_rate]);
  // 2026-08-09 (рішення власника): для валютного дока — колонка
  // грн-еквіваленту рядка (номінал × курс НБУ/1С).
  const isFxDoc = !!docQuery.data?.currency_code
    && docQuery.data.currency_code !== 'UAH';
  const fxNum = Number(fxRate) || 0;

  // 2026-07-27 (UX як в УТП): якщо в організації РІВНО ОДИН склад —
  // підставляємо його автоматично (склад обов'язковий для товарних рядків).
  useEffect(() => {
    if (isPosted || form.warehouse_id) return;
    if (warehousesQ.data?.length === 1) {
      setForm((f) => f.warehouse_id ? f : { ...f, warehouse_id: warehousesQ.data![0]!.id });
    }
  }, [warehousesQ.data, isPosted, form.warehouse_id]);


  // П1 (2026-07-23): /goods-receipts/new?basis=<order_id> — кнопка «створити
  // надходження» із замовлення постачальнику: шапка + рядки-залишки.
  const basisId = searchParams.get('basis');
  // ч.1352 (обернений ланцюг): /goods-receipts/new?tax_invoice=<id> — надходження З вхідної ПН
  // (накладну з ЄРПН прийняли раніше); створене надходження стає її підставою.
  const pnBasisId = searchParams.get('tax_invoice');
  // ч.1359 (обернений ланцюг з оплати): /goods-receipts/new?bank_payment=<id> — надходження
  // ЗІ списання банку: шапка з платежу; непроведене списання отримує «На підставі».
  const bankPaymentId = searchParams.get('bank_payment');
  const linkBank = async (receiptId: string) => {
    if (!bankPaymentId) return;
    const problem = await linkBankDoc({ kind: 'payment', bankId: bankPaymentId, createdType: 'doc.goods_receipts',
      createdId: receiptId, field: 'goods_receipt_id' });
    if (problem) setError(problem);
    void qc.invalidateQueries({ queryKey: ['related-docs'] });
  };
  const linkPn = async (receiptId: string) => {
    if (!pnBasisId) return;
    try {
      await taxInvoicesIncoming.linkBase(pnBasisId, {
        base_document_type: 'doc.goods_receipts', base_document_id: receiptId });
    } catch (e) {
      setError(`Надходження створено, але привʼязати його до ПН не вдалося: ${extractError(e)}`);
    }
  };
  const pnApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !pnBasisId || pnApplied.current) return;
    if (!vatList.data || !nomList.data) return;
    pnApplied.current = true;
    void (async () => {
      try {
        const pn = await taxInvoicesIncoming.get(pnBasisId);
        const live = pn.items.filter((it) => it.nomenclature_id && Number(it.quantity) > 0);
        const goods: GoodsReceiptItem[] = live
          .filter((it) => !isServiceNom(it.nomenclature_id!))
          .map((it, idx) => ({
            ...newRow(idx + 1, it.vat_rate_id, defaultTaxPurposeId),
            nomenclature_id: it.nomenclature_id!,
            quantity: String(Number(it.quantity)),
            price: String(Number(it.price_without_vat)),
            amount_without_vat: Number(it.base_amount).toFixed(2),
            vat_rate_id: it.vat_rate_id,
            vat_amount: Number(it.vat_amount).toFixed(2),
            total_amount: (Number(it.base_amount) + Number(it.vat_amount)).toFixed(2),
          }));
        const services: GoodsReceiptService[] = live
          .filter((it) => isServiceNom(it.nomenclature_id!))
          .map((it, idx) => ({
            line_number: idx + 1,
            nomenclature_id: it.nomenclature_id,
            description: it.description || nomList.data?.items.find((n) => n.id === it.nomenclature_id)?.name || '',
            quantity: String(Number(it.quantity)),
            price: String(Number(it.price_without_vat)),
            amount_without_vat: Number(it.base_amount).toFixed(2),
            vat_rate_id: it.vat_rate_id,
            vat_amount: Number(it.vat_amount).toFixed(2),
            total_amount: (Number(it.base_amount) + Number(it.vat_amount)).toFixed(2),
            cost_account_code: '92',
            tax_purpose_id: taxPurposeByNomId.get(it.nomenclature_id ?? '') ?? defaultTaxPurposeId,
          }));
        setForm((f) => ({
          ...f,
          organization_id: pn.organization_id,
          contractor_id: pn.contractor_id ?? f.contractor_id,
          contract_id: pn.contract_id ?? f.contract_id,
          document_date: pn.invoice_date ? `${pn.invoice_date}T12:00` : f.document_date,
          items: goods,
          services,
        }));
        if (goods.length === 0 && services.length > 0) setLinesTab('services');
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, pnBasisId, vatList.data, nomList.data]);
  const bankApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !bankPaymentId || bankApplied.current) return;
    bankApplied.current = true;
    void (async () => {
      try {
        const p = await bankPrefill('payment', bankPaymentId);
        setForm((f) => ({
          ...f,
          organization_id: p.organization_id || f.organization_id,
          contractor_id: p.contractor_id || f.contractor_id,
          contract_id: p.contract_id || f.contract_id,
          document_date: p.document_date || f.document_date,
          comment: f.comment || paymentNote('payment', p),
        }));
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, bankPaymentId]);
  const basisApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !basisId || basisApplied.current) return;
    if (!vatList.data || !nomList.data) return;
    basisApplied.current = true;
    void (async () => {
      try {
        const order = await supplierOrders.get(basisId);
        const bItems = orderToRows(order);
        const bServices = orderToServices(order);
        setForm((f) => ({
          ...f,
          organization_id: order.organization_id,
          contractor_id: order.contractor_id,
          contract_id: order.contract_id ?? '',
          warehouse_id: order.warehouse_id ?? f.warehouse_id,
          supplier_order_id: order.id,
          vat_flags: orderVatFlags(order),  // ч.1374: як у рахунку-підставі
          items: bItems,
          services: bServices,
        }));
        vatFlagsTouched.current = true;
        if (bItems.length === 0 && bServices.length > 0) setLinesTab('services');
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, basisId, vatList.data, nomList.data]);

  // П1: автовибір основного договору контрагента (is_main, 0373).
  useMainContractAutoSelect({
    // 2026-07-25: і при редагуванні DRAFT — зміна контрагента скидає договір,
    // основний нового контрагента підставляється (posted-док не зачіпається:
    // у нього contract_id завжди заповнений — хук мовчить).
    enabled: true,
    contractorId: form.contractor_id,
    contractId: form.contract_id,
    contracts: contractsQ.data,
    onSelect: (cid) => setForm((f) => (f.contract_id ? f : { ...f, contract_id: cid })),
  });

  const svcWithoutVat = form.services.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const svcVat = form.services.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0)
    + svcWithoutVat;
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0) + svcVat;
  const totalAmount = totalWithoutVat + totalVat;
  // ч.750 (рішення власника): «Разом» — по вкладці (товари / обладнання /
  // послуги), «Усього» — по всьому документу (товари + обладнання + послуги).
  const tabItems = form.items.filter((it) => (linesTab === 'equipment') === isEquipRow(it));
  const tabWithoutVat = tabItems.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const tabVat = tabItems.reduce((s, e) => s + Number(e.vat_amount || 0), 0);

  function payloadServices(): GoodsReceiptService[] {
    return form.services
      .filter((sv) => Number(sv.amount_without_vat) > 0 || (sv.description ?? '').trim()
        || sv.nomenclature_id)
      .map((sv, idx) => ({ ...sv, line_number: idx + 1 }));
  }

  function setService(i: number, patch: Partial<GoodsReceiptService>) {
    setForm((f) => ({
      ...f,
      services: f.services.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        const rate = f.vat_flags.vat_considered ? rateOf(next.vat_rate_id) : 0;
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch || 'vat_amount' in patch) {
          // ч.1374: за ознаками документа — ціни з ПДВ / без, ПДВ вручну.
          const a = lineAmounts(Number(next.quantity), Number(next.price), rate, f.vat_flags,
            { manualVat: 'vat_amount' in patch ? Number(next.vat_amount) : null });
          next.amount_without_vat = a.amount_without_vat;
          next.vat_amount = a.vat_amount;
          next.total_amount = a.total_amount;
        } else if ('amount_without_vat' in patch) {
          const awv = Number(next.amount_without_vat) || 0;
          const vatAmt = (awv * rate) / 100;
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  // ч.914: рядки послуг з попапа підбору / швидкого створення номенклатури.
  function addServiceRows(nomIds: string[]) {
    setForm((f) => ({
      ...f,
      services: [...f.services, ...nomIds.map((nid, k) => ({
        line_number: f.services.length + 1 + k,
        nomenclature_id: nid,
        description: nomList.data?.items.find((n) => n.id === nid)?.name ?? '',
        quantity: '1', price: '',
        amount_without_vat: '0',
        vat_rate_id: showVatColumns
          ? (vatRateByNomId.get(nid) ?? effectiveDefaultVatId ?? null)
          : (exemptVatId || null),
        vat_amount: '0', total_amount: '0', cost_account_code: '92',
        tax_purpose_id: taxPurposeByNomId.get(nid) ?? defaultTaxPurposeId,
      }))],
    }));
  }
  function removeServiceRow(i: number) {
    setForm((f) => ({
      ...f,
      services: f.services.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const create = useMutation({
    mutationFn: () => goodsReceipts.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,  // ч.912: лише послуги — без складу
      supplier_order_id: form.supplier_order_id || null,
      supplier_doc_number: form.supplier_doc_number || null,
      ...form.vat_flags,
      advance_offset_mode: form.advance_offset_mode,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
      services: payloadServices(),
    }),
    onSuccess: (doc) => {
      void linkPn(doc.id);
      void linkBank(doc.id);
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
      navigate(`/goods-receipts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsReceipts.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,  // ч.912: лише послуги — без складу
      supplier_order_id: form.supplier_order_id || null,
      supplier_doc_number: form.supplier_doc_number || null,
      ...form.vat_flags,
      advance_offset_mode: form.advance_offset_mode,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
      services: payloadServices(),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-receipt', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => goodsReceipts.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-receipt', id] });
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsReceipts.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-receipt', id] });
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsReceipts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
      navigate('/goods-receipts');
    },
  });

  function setItem(i: number, patch: Partial<GoodsReceiptItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // авто-пересчёт суммы и НДС (ч.1374: за ознаками документа — ціни з ПДВ / без,
        // ПДВ вручну при вимкненому авторозрахунку)
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch || 'vat_amount' in patch) {
          const a = lineAmounts(Number(next.quantity), Number(next.price), rateOf(next.vat_rate_id), f.vat_flags, {
            discountPercent: Number((next as { discount_percent?: string | null }).discount_percent ?? 0),
            manualVat: 'vat_amount' in patch ? Number(next.vat_amount) : null,
          });
          next.amount_without_vat = a.amount_without_vat;
          next.vat_amount = a.vat_amount;
          next.total_amount = a.total_amount;
        }
        return next;
      }),
    }));
  }
  function addRowFromPicker(picked: GoodsBalanceRow) {
    if (linesTab === 'services') {
      addServiceRows([picked.nomenclature_id]);
      setPickerOpen(false);
      return;
    }
    setForm((f) => {
      const nextLine = f.items.length + 1;
      const row: GoodsReceiptItem = {
        ...newRow(nextLine, effectiveDefaultVatId, defaultTaxPurposeId),
        nomenclature_id: picked.nomenclature_id,
        // Auto-fill uom_id з base_uom_id (2026-06-02).
        // uom_id: NULL = базова одиниця (FK на nomenclature_uoms, НЕ класифікатор!)
        uom_id: null,
        // Етап 2.1: ставка ПДВ з картки (fallback — дефолт документа).
        vat_rate_id: showVatColumns
          ? (vatRateByNomId.get(picked.nomenclature_id) ?? defaultVatId)
          : exemptVatId,
        tax_purpose_id: taxPurposeByNomId.get(picked.nomenclature_id) ?? defaultTaxPurposeId,
        // Вкладка «Обладнання»: авто-рахунок 152 (капітальні інвестиції).
        account_code: linesTab === 'equipment' ? '152' : null,
      };
      return { ...f, items: [...f.items, row] };
    });
    setPickerOpen(false);
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // Зберегти і одразу провести (на новому — create→post, на збереженому — post).
  async function onSaveAndPost() {
    setError(null);
    try {
      if (isNew) {
        const doc = await create.mutateAsync();
        await goodsReceipts.post(doc.id);
        qc.invalidateQueries({ queryKey: ['goods-receipts'] });
      } else {
        await update.mutateAsync();
        await post.mutateAsync();
      }
    } catch (e) {
      setError(extractError(e));
    }
  }

  // Рядки з замовлення постачальнику: залишок (quantity - quantity_received).
  // Спільне ядро для «Заповнити з підстави» і ?basis=<order_id> (П1).
  // 2026-07-31 (власник: «з рахунку на послуги заповнює товари»): рядки
  // рахунку діляться за видом номенклатури — товари → ТЧ «Товари»,
  // послуги → ТЧ «Послуги» (Дт витрат — Кт 631, дефолт 92).
  const isServiceNom = (nid: string) =>
    !!nomList.data?.items.find((n) => n.id === nid)?.is_service;

  function orderToRows(order: Awaited<ReturnType<typeof supplierOrders.get>>): GoodsReceiptItem[] {
    return order.items
      .filter((it) => !isServiceNom(it.nomenclature_id))
      .map((it, idx): GoodsReceiptItem | null => {
        const remaining = Number(it.quantity) - Number(it.quantity_received ?? 0);
        if (remaining <= 0) return null;
        const price = Number(it.price);
        // ч.1374: суми — за ознаками рахунку-підстави (ціна там з ПДВ або без).
        const a = lineAmounts(remaining, price, rateOf(it.vat_rate_id), orderVatFlags(order));
        return {
          line_number: idx + 1,
          nomenclature_id: it.nomenclature_id,
          quantity: String(remaining),
          price: String(price),
          amount_without_vat: a.amount_without_vat,
          vat_rate_id: it.vat_rate_id,
          vat_amount: a.vat_amount,
          total_amount: a.total_amount,
          // Рахунок рядка рахунку/замовлення (вкладка «Обладнання» → 152)
          // переноситься — прихід покладе рядок на свою вкладку.
          account_code: (it as { account_code?: string | null }).account_code ?? null,
          // Призначення ПДВ — як у ручних рядках: з картки, інакше TAXABLE.
          tax_purpose_id: taxPurposeByNomId.get(it.nomenclature_id ?? '') ?? defaultTaxPurposeId,
        };
      })
      .filter((x): x is GoodsReceiptItem => x !== null);
  }

  function orderToServices(order: Awaited<ReturnType<typeof supplierOrders.get>>): GoodsReceiptService[] {
    return order.items
      .filter((it) => isServiceNom(it.nomenclature_id))
      .map((it, idx) => {
        const qty = Number(it.quantity);
        const price = Number(it.price);
        const a = lineAmounts(qty, price, rateOf(it.vat_rate_id), orderVatFlags(order));  // ч.1374
        const nm = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
        return {
          line_number: idx + 1,
          nomenclature_id: it.nomenclature_id,
          description: (it as { service_description?: string | null }).service_description
            || nm?.name || '',
          quantity: String(qty),
          price: String(price),
          amount_without_vat: a.amount_without_vat,
          vat_rate_id: it.vat_rate_id,
          vat_amount: a.vat_amount,
          total_amount: a.total_amount,
          cost_account_code: '92',
          tax_purpose_id: taxPurposeByNomId.get(it.nomenclature_id ?? '') ?? defaultTaxPurposeId,
        };
      });
  }

  async function applyFromInvoice(orderId: string) {
    try {
      const order = await supplierOrders.get(orderId);
      const newItems = orderToRows(order);
      const newServices = orderToServices(order);
      if (newItems.length === 0 && newServices.length === 0) {
        setError('Рахунок повністю отримано — немає що додавати.');
        setInvoicePickerOpen(false);
        return;
      }
      setForm((f) => ({
        ...f,
        organization_id: order.organization_id,
        contractor_id: order.contractor_id,
        contract_id: order.contract_id ?? '',
        warehouse_id: f.warehouse_id || (order.warehouse_id ?? ''),
        supplier_order_id: order.id,
        vat_flags: orderVatFlags(order),  // ч.1374: як у рахунку-підставі
        items: newItems,
        services: newServices,
      }));
      // Рахунок лише на послуги → одразу вкладка «Послуги».
      if (newItems.length === 0 && newServices.length > 0) setLinesTab('services');
      setError(null);
      setInvoicePickerOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('gr.new')}</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати надходження товарів у жодній з
            ваших організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-receipts')}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  // ч.859: «ще дії» → права колонка. Склад: вхідна ПН, дод. витрати, повернення,
  // платіжка/РКО постачальнику (moneyDocActions) + «створити ВМД» (лише валютний
  // проведений прихід — раніше була кнопкою у рядку вкладок).
  const railActions = [
            // ч.1347 (рішення власника): документи «на підставі» — зі збереженого,
            // проведення підстави не чекаємо (повернення перевірить це при проведенні).
            { // ч.1352 (обернений ланцюг): рахунок постачальника З надходження.
              label: 'створити рахунок постачальника',
              title: isNew ? 'Спершу збережіть надходження'
                : 'Рахунок постачальника з рядків цього надходження; документи звʼязуються',
              disabled: isNew,
              onClick: () => navigate(`/supplier-invoices/new?from_receipt=${id}`) },
            { label: 'створити вхідну ПН',
              title: isNew ? 'Спершу збережіть надходження'
                : 'Зареєструвати отриману від постачальника ПН (рядки з прихода)',
              disabled: isNew,
              onClick: () => navigate(`/tax-invoices-incoming/new?basis=${id}`) },
            { label: 'створити дод. витрати',
              title: isNew ? 'Спершу збережіть надходження'
                : 'ТЗР/страхування/брокер — розподіл на собівартість рядків цього прихода',
              disabled: isNew,
              onClick: () => navigate(`/additional-expenses/new?receipt=${id}`) },
            { label: 'створити повернення',
              title: isNew ? 'Спершу збережіть надходження'
                : 'Повернення товару постачальнику (залишок до повернення)',
              disabled: isNew,
              onClick: () => navigate(`/goods-returns-to-supplier/new?basis=${id}`) },
            // ч.740: платіжка / РКО постачальнику на суму надходження.
            ...moneyDocActions({ navigate, isNew, direction: 'out', prefill: {
              org: form.organization_id, contractor: form.contractor_id, contract: form.contract_id,
              order: form.supplier_order_id, project: form.project_id, op: 'TO_SUPPLIER',
              amount: totalWithoutVat + totalVat,
              purpose: `Оплата за товар згідно з накладною № ${form.document_number} від ${fmtDocDate(form.document_date)}${vatTail(totalVat)}`,
            } }),
            ...(isFxDoc ? [{
              label: 'створити ВМД',
              title: isNew ? 'Спершу збережіть надходження'
                : 'Імпорт: ВМД на підставі цього прихода (мито/збір/ПДВ на митниці; податковий кредит — за ВМД, ряд. 11.1 декларації)',
              disabled: isNew,
              onClick: () => navigate(`/customs-declarations/new?receipt=${id}`),
            }] : []),
  ];

  // ч.859: прогрес — ЛИШЕ обовʼязкові поля.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Постачальник і договір', required: 1,
      filled: form.contractor_id ? 1 : 0 },
    // ч.912: склад обовʼязковий лише за наявності товарних рядків.
    { id: 'wh', title: 'Склад і накладна', required: form.items.some((it) => it.nomenclature_id) ? 1 : 0,
      filled: form.items.some((it) => it.nomenclature_id) && form.warehouse_id ? 1 : 0 },
    { id: 'items', title: 'Товари / Послуги', required: 1,
      filled: (form.items.length + form.services.length) > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? t('gr.new') : `${t('gr.title')}: ${form.document_number}`}
          {docQuery.data?.currency_code && docQuery.data.currency_code !== 'UAH' && (
            <span style={{ marginLeft: '0.75rem', padding: '0.1rem 0.5rem',
              background: 'var(--badge-blue-bg)', color: 'var(--badge-blue-fg)', borderRadius: 4,
              fontSize: '0.85rem', fontWeight: 700 }}
              title="Валютний (імпортний) документ — суми у валюті договору">
              {docQuery.data.currency_code}
            </span>
          )}
        </h2>
        {/* Ланцюжок документів з прихода (2026-07-29) + тулбар — одним
            рядком СПРАВА (рішення власника 2026-07-31, як у реалізації). */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem',
          alignItems: 'center', flexWrap: 'wrap' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/goods-receipts')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={onSaveAndPost}
          postDisabled={!isNew && form.items.length === 0}
          postPending={create.isPending || update.isPending || post.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => {
            confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); });
          }}
          deletePending={remove.isPending}
          entriesDoc={{ type: 'doc.goods_receipts', id: isNew ? '' : id! }}
          // ч.859: доп-дії — у правій колонці («Повʼязані дії»), ЖОДНА не загублена.
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {/* ч.859 (новий дизайн, Закон #10): блоки + права рейка. */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-receipts/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_receipts', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('gr.organization')} *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('gr.number')}</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('gr.date')} *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        {/* ч.743 (рішення власника): «Напрям» — одразу після Дати. */}
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>

      </DocBlock>

      <DocBlock index={2} title="Постачальник і договір">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('gr.contractor')} *
            <QuickCreateLink title="Створити нового контрагента"
              disabled={isPosted}
              onClick={() => setQuickContractor(true)} />
          </label>
          <SearchSelect
            items={(supplierList.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            onChange={(id) => setForm({ ...form, contractor_id: id, contract_id: '' })}
            disabled={isPosted || !!form.supplier_order_id}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            {t('gr.contract')}{' '}
            <small style={{ color: 'var(--muted)' }}>(авто вкл.)</small>
            {form.contractor_id && (
              <QuickCreateLink
                disabled={isPosted || !form.contractor_id}
                title={form.contractor_id ? 'Створити договір цього контрагента' : 'Спершу оберіть контрагента'}
                onClick={() => setQuickContract(true)} />
            )}
          </label>
          <select
            value={form.contract_id}
            onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
            disabled={isPosted || !form.contractor_id}
          >
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        {/* 2026-07-31 (рішення власника): «створити з рахунку» — одразу
            ПІСЛЯ Договору; єдина механіка «Підстава» (лише АКТИВНІ рахунки
            постачальника; замовлення привʼязується кнопкою зі своєї
            сторінки). */}
        <div className="field" style={{ gridColumn: 'span 4' }}>
          {/* ч.859: «Підстава» — назва поля, документ під нею; кнопка — лише
              поки звʼязку немає (як у реалізації, ч.854/855). */}
          <label>{form.supplier_order_id ? 'Підстава' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {!form.supplier_order_id && (
          <button type="button" className="btn-doc"
            onClick={() => setInvoicePickerOpen(true)}
            disabled={isPosted || !form.organization_id}
            title={!form.organization_id
                ? 'Спершу оберіть організацію'
                : 'Заповнити надходження з активного рахунку постачальника'}>
            <DocIcon />створити з рахунку
          </button>
          )}
          {form.supplier_order_id && (() => {
            const basisDoc = basisOrderQ.data;
            const isOrder = basisDoc?.order_kind === 'ORDER';
            return (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                  onClick={() => navigate(`${isOrder ? '/supplier-orders' : '/supplier-invoices'}/${form.supplier_order_id}`)}>
                  {isOrder ? 'замовлення' : 'рахунок'}
                  {basisDoc
                    ? ` №${basisDoc.document_number} від ${new Date(basisDoc.document_date).toLocaleDateString('uk-UA')}`
                    : ''}
                </a>
                {!isPosted && (
                  <button type="button" className="btn-x"
                    onClick={() => setForm({ ...form, supplier_order_id: '', items: [] })}
                    title="Зняти звʼязок з рахунком/замовленням і очистити рядки">×</button>
                )}
              </span>
            );
          })()}
          </div>
        </div>
      </div>

      {/* 2026-07-31 (рішення власника): Склад + Накладна пост. — однією
          строкою (ч.743: «Напрям» переїхав у шапку після Дати). */}
      </DocBlock>

      <DocBlock index={3} title="Склад і накладна">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label title={form.items.some((it) => it.nomenclature_id) ? undefined : 'Для надходження лише послуг склад не потрібен'}>
            {t('gr.warehouse')}{form.items.some((it) => it.nomenclature_id) ? ' *' : ''}</label>
          <select
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>{t('gr.supplier_doc')}</label>
          <input
            value={form.supplier_doc_number}
            onChange={(e) => setForm({ ...form, supplier_doc_number: e.target.value })}
            disabled={isPosted}
          />
        </div>
      </div>


      </DocBlock>

      <DocBlock index={4} title="Товари / Послуги"
        actions={!isPosted ? (<>
          <VatFlagsButton value={form.vat_flags} onChange={applyVatFlags}
            isVatPayer={docVatPayer} />
          {/* ч.914 (рішення власника): на вкладці «Послуги» — ті самі дві кнопки
              і попап підбору послуги, як у реалізації. */}
          <button onClick={() => setQuickNomOpen(true)} className="btn-doc"
            title="Створити нову номенклатуру (мінімум полів) і одразу підставити рядком">
            <DocIcon />створити номенклатуру
          </button>
          <button className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id
              ? 'Спершу оберіть юрособу'
              : linesTab === 'services'
                ? 'Підбір послуги з довідника: Дт «рахунок витрат» — Кт 631 (без складського руху)'
                : 'Підбір з довідника номенклатури'}>
            {linesTab === 'services' ? '+ додати послугу'
              : linesTab === 'equipment' ? '+ додати актив' : '+ додати товар'}
          </button>
        </>) : undefined}>
      {/* 2026-07-31 (рішення власника): вкладки як у реалізації. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'lines' && linesTab === 'goods' ? 'active' : ''}
            onClick={() => { setViewTab('lines'); setLinesTab('goods'); }}>
            Товари ({form.items.filter((it) => !isEquipRow(it)).length})</button>
          <button type="button" className={viewTab === 'lines' && linesTab === 'services' ? 'active' : ''}
            onClick={() => { setViewTab('lines'); setLinesTab('services'); }}>
            Послуги ({form.services.length})</button>
          <button type="button" className={viewTab === 'lines' && linesTab === 'equipment' ? 'active' : ''}
            onClick={() => { setViewTab('lines'); setLinesTab('equipment'); }}>
            Необоротні активи ({form.items.filter(isEquipRow).length})</button>
        </div>
        {/* 2026-08-09 (рішення власника): валюта документа — одразу за
            вкладками (лише позначення; курс і грн — під ТЧ). */}
        {docQuery.data?.currency_code && docQuery.data.currency_code !== 'UAH' && (
          <span style={{ padding: '0.15rem 0.5rem', background: 'var(--badge-blue-bg)',
            color: 'var(--badge-blue-fg)', borderRadius: 4, fontSize: '0.85rem',
            fontWeight: 700 }}
            title="Валюта документа (суми ТЧ — у валюті)">
            {docQuery.data.currency_code}
          </span>
        )}
        {/* ч.859: «створити ВМД» переїхала у праву рейку («Повʼязані дії»). */}
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
          <button type="button" className={viewTab === 'tax' ? 'active' : ''}
            onClick={() => setViewTab('tax')}>Податковий облік</button>
        </div>
        {/* дт-кт поруч з вкладками рахунків (рішення власника 2026-08-09). */}
      </div>
      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          {/* П4b: залік авансу — тут, як у реалізації. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isPosted ? 'default' : 'pointer', margin: '0.5rem 0' }}>
            <input type="checkbox"
              checked={form.advance_offset_mode === 'NONE'}
              disabled={isPosted}
              onChange={(e) => setForm({ ...form, advance_offset_mode: e.target.checked ? 'NONE' : 'AUTO' })} />
            <span>не зараховувати аванс автоматично
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
                (типово аванс за договором зараховується при проведенні)
              </span>
            </span>
          </label>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 180 }}
                    title="Рахунок запасів рядка (порожньо — авто: правило/картка/281)">
                  Рах. обліку (запаси)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.nomenclature_id).map((it) => {
                const i = form.items.indexOf(it);
                const nomInfo = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
                return (
                  <tr key={`g${i}`}>
                    <td>{nomInfo?.name ?? '—'}</td>
                    <td>
                      <input value={it.account_code ?? ''} maxLength={10}
                        onChange={(e) => setItem(i, { account_code: e.target.value || null })}
                        disabled={isPosted}
                        placeholder={previewByNom.get(it.nomenclature_id) ?? '281'}
                        style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
              {form.services.map((sv, i) => {
                const nomInfo = sv.nomenclature_id
                  ? nomList.data?.items.find((n) => n.id === sv.nomenclature_id) : null;
                return (
                  <tr key={`s${i}`}>
                    <td>
                      {nomInfo?.name ?? sv.description ?? 'Послуга'}
                      <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: '0.78rem' }}>
                        (послуга: Дт витрат — Кт 631)
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <select value={sv.cost_account_code ?? '92'} disabled={isPosted}
                          onChange={(e) => setService(i, { cost_account_code: e.target.value })}>
                          {COST_ACCOUNTS.map(([code, name]) => (
                            <option key={code} value={code}>{name}</option>
                          ))}
                        </select>
                        <select value={sv.cost_item_id ?? ''} disabled={isPosted}
                          title="Стаття витрат — аналітика рахунку витрат (авто з картки номенклатури)"
                          onChange={(e) => setService(i, { cost_item_id: e.target.value || null })}
                          style={{ maxWidth: 260 }}>
                          <option value="">— стаття витрат не задана —</option>
                          {costItemsQ.data?.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Сірим показано рахунки автоматичного підбору (правила / картка /
            типовий 281) — впишіть свій код, щоб перебити.
          </p>
        </div>
      )}
      {viewTab === 'tax' && (
        <TchTable style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ textTransform: 'none' }}>
              <th>Номенклатура</th>
              <th style={{ width: 160 }}>Ставка ПДВ</th>
              <th style={{ width: 240 }}
                  title="Призначення ПДВ-кредиту (пропорційний розподіл, ПКУ ст.199)">
                Призначення ПДВ</th>
            </tr>
          </thead>
          <tbody>
            {form.items.filter((it) => it.nomenclature_id).map((it) => {
              const i = form.items.indexOf(it);
              const nomInfo = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
              return (
                <tr key={i}>
                  <td>{nomInfo?.name ?? '—'}</td>
                  <td>
                    {showVatColumns ? (
                      <select value={it.vat_rate_id}
                        onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                        disabled={isPosted}>
                        {vatList.data?.map((v) => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                    ) : <span style={{ color: 'var(--muted)' }}>Без ПДВ</span>}
                  </td>
                  <td>
                    <select value={it.tax_purpose_id ?? ''} disabled={isPosted}
                      style={{ width: '100%' }}
                      onChange={(e) => setItem(i, { tax_purpose_id: e.target.value || null })}>
                      <option value="">— не вказано —</option>
                      {taxPurposesList.data?.map((tp) => (
                        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
            {/* Рядки-послуги: ставка ПДВ редагується; призначення ПДВ у
                послуг прихода немає (окрема ТЧ без поля). */}
            {form.services.map((sv, i) => {
              const nomInfo = sv.nomenclature_id
                ? nomList.data?.items.find((n) => n.id === sv.nomenclature_id) : null;
              return (
                <tr key={`s${i}`}>
                  <td>
                    {nomInfo?.name ?? sv.description ?? 'Послуга'}
                    <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: '0.78rem' }}>
                      (послуга)
                    </span>
                  </td>
                  <td>
                    {showVatColumns ? (
                      <select value={sv.vat_rate_id ?? ''}
                        onChange={(e) => setService(i, { vat_rate_id: e.target.value || null })}
                        disabled={isPosted}>
                        <option value="">—</option>
                        {vatList.data?.map((v) => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                    ) : <span style={{ color: 'var(--muted)' }}>Без ПДВ</span>}
                  </td>
                  <td>
                    <select value={sv.tax_purpose_id ?? ''} disabled={isPosted}
                      style={{ width: '100%' }}
                      onChange={(e) => setService(i, { tax_purpose_id: e.target.value || null })}>
                      <option value="">— не вказано —</option>
                      {taxPurposesList.data?.map((tp) => (
                        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      {viewTab === 'lines' && (linesTab === 'goods' || linesTab === 'equipment') && (<>
      {linesTab === 'equipment' && (
        <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.5rem 0 0.25rem' }}>
          Рядки обладнання приходуються на <b>152 «Капітальні інвестиції»</b>
          (не у товарний оборот). Після проведення створіть картку ОЗ і
          введіть в експлуатацію (Дт 10х — Кт 152) — документ
          «Введення в експлуатацію» у меню Активи.
        </p>
      )}
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>{t('gr.nomenclature')}</th>
            {linesTab === 'goods' && <th style={{ width: 150 }}>Склад</th>}
            <th className="num" style={{ width: 90 }}>{t('gr.qty')}</th>
            <th className="num" style={{ width: 110 }}
                title={form.vat_flags.prices_include_vat ? 'Ціна рядка вже з ПДВ («сума включає ПДВ»)' : undefined}>
              {form.vat_flags.prices_include_vat ? 'Ціна з ПДВ' : t('gr.price')}
            </th>
            <th className="num" style={{ width: 130 }}>
              {showVatColumns ? t('gr.amount_without_vat') : 'Сума'}
            </th>
            {isFxDoc && (
              <th className="num" style={{ width: 130 }}
                  title="Гривневий еквівалент рядка за курсом (номінал × курс). Тягнеться у ВМД як митна вартість.">
                За курсом НБУ, грн
              </th>
            )}
            {showVatColumns && (
              <>
                <th style={{ width: 100 }}>{t('gr.vat')}</th>
                <th className="num" style={{ width: 110 }}>{t('gr.vat_amount')}</th>
                <th className="num" style={{ width: 120 }}>{t('gr.total')}</th>
              </>
            )}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            // Вкладки: «Товари» ховає рядки-обладнання і навпаки.
            if ((linesTab === 'equipment') !== isEquipRow(it)) return null;
            const showSeries = !!it.nomenclature_id && useSeriesMap.get(it.nomenclature_id);
            const showChar = !!it.nomenclature_id && useCharMap.get(it.nomenclature_id);
            return (
            <Fragment key={i}>
            <tr>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  onChange={(nomId) => {
                    setItem(i, {
                      nomenclature_id: nomId,
                      // Auto-fill uom_id (2026-06-02).
                      uom_id: null,
                      // Етап 2.1: ставка ПДВ з картки (пересчёт vat_amount у setItem).
                      vat_rate_id: showVatColumns
                        ? (vatRateByNomId.get(nomId) ?? it.vat_rate_id)
                        : exemptVatId,
                      tax_purpose_id: taxPurposeByNomId.get(nomId) ?? it.tax_purpose_id,
                      // Підфіча #4: скидаємо серію/характеристику при зміні номенклатури.
                      series_id: null,
                      characteristic_id: null,
                    });
                  }}
                />
              </td>
              {linesTab === 'goods' && (
                <td>
                  <select value={it.warehouse_id ?? ''}
                          onChange={(e) => setItem(i, { warehouse_id: e.target.value || null })}
                          disabled={isPosted} style={{ width: '100%' }}>
                    <option value="">— за замовчуванням —</option>
                    {warehousesQ.data?.map((w) => (
                      <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                    ))}
                  </select>
                </td>
              )}
              <td>
                <input type="number" step="0.001" value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: e.target.value })}
                  disabled={isPosted} />
              </td>
              <td>
                <input type="number" step="0.01" value={it.price}
                  onChange={(e) => setItem(i, { price: e.target.value })}
                  disabled={isPosted} />
              </td>
              <td style={{ textAlign: 'right' }}>
                {fmt(Number(it.amount_without_vat))}
              </td>
              {isFxDoc && (
                <td style={{ textAlign: 'right', color: fxNum ? '#1e40af' : 'var(--muted)', fontWeight: 600 }}>
                  {fxNum ? fmt(Number(it.amount_without_vat) * fxNum) : '—'}
                </td>
              )}
              {showVatColumns && (
                <>
                  <td>
                    <select
                      value={it.vat_rate_id}
                      onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                      disabled={isPosted}
                    >
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  {/* Призначення ПДВ переїхало у вкладку «Податковий облік»
                      (2026-07-31, як у реалізації). */}
                  <td style={{ textAlign: 'right' }}>
                    {!form.vat_flags.vat_auto && !isPosted ? (
                      <input type="number" step="0.01" min="0" value={it.vat_amount}
                             title="Сума ПДВ вручну (авторозрахунок ПДВ вимкнено)"
                             onChange={(e) => setItem(i, { vat_amount: e.target.value })} />
                    ) : fmt(Number(it.vat_amount))}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {fmt(Number(it.total_amount))}
                  </td>
                </>
              )}
              <td style={{ whiteSpace: 'nowrap' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
                {isPosted && linesTab === 'equipment' && (
                  <button style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                    title="Створити картку ОЗ з цього рядка (первісна вартість = сума без ПДВ)"
                    onClick={() => setQuickFa({
                      name: nomList.data?.items.find((n) => n.id === it.nomenclature_id)?.name ?? '',
                      cost: it.amount_without_vat,
                    })}>
                    картка НА
                  </button>
                )}
              </td>
            </tr>
            {(showSeries || showChar) && (
              <tr style={{ background: 'var(--badge-amber-bg)', fontSize: '0.8rem' }}>
                <td></td>
                <td colSpan={showVatColumns ? 8 : 5}>
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {showSeries && (
                      <label>
                        <span style={{ color: 'var(--badge-amber-fg)', marginRight: '0.3rem' }}>Серія:</span>
                        <select
                          value={it.series_id ?? ''}
                          onChange={(e) => setItem(i, { series_id: e.target.value || null })}
                          disabled={isPosted}
                          style={{ minWidth: 220 }}
                        >
                          <option value="">— оберіть серію —</option>
                          {(seriesByNom[it.nomenclature_id] ?? []).map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}{s.code ? ` — ${s.code}` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {showChar && (
                      <label>
                        <span style={{ color: 'var(--badge-amber-fg)', marginRight: '0.3rem' }}>Характеристика:</span>
                        <select
                          value={it.characteristic_id ?? ''}
                          onChange={(e) => setItem(i, { characteristic_id: e.target.value || null })}
                          disabled={isPosted}
                          style={{ minWidth: 220 }}
                        >
                          <option value="">— оберіть характеристику —</option>
                          {(charByNom[it.nomenclature_id] ?? []).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}{c.code ? ` — ${c.code}` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          {/* ч.750: «Разом» — вкладка, «Усього» — весь документ (з послугами).
              colSpan: #, Номенклатура, [Склад], К-сть, Ціна. */}
          <tr style={{ fontWeight: 500 }}>
            <td colSpan={linesTab === 'goods' ? 5 : 4} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabWithoutVat)}</td>
            {isFxDoc && (
              <td style={{ textAlign: 'right', color: 'var(--badge-blue-fg)' }}>
                {fxNum ? fmt(tabWithoutVat * fxNum) : '—'}
              </td>
            )}
            {showVatColumns && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(tabVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(tabWithoutVat + tabVat)}</td>
              </>
            )}
            <td></td>
          </tr>
          <tr>
            <td colSpan={linesTab === 'goods' ? 5 : 4} style={{ textAlign: 'right' }}>{t('gr.total')}:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {isFxDoc && (
              <td style={{ textAlign: 'right', color: 'var(--badge-blue-fg)' }}>
                {fxNum ? fmt(totalWithoutVat * fxNum) : '—'}
              </td>
            )}
            {showVatColumns && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      {/* 2026-08-09 (рішення власника): курс і гривневий еквівалент —
          ПІД табличною частиною. Мігрований док несе курс з 1С;
          наш — кнопка «отримати курс НБУ» на дату. */}
      {docQuery.data?.currency_code && docQuery.data.currency_code !== 'UAH' && (
        <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center',
          gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.88rem',
          padding: '0.45rem 0.75rem', borderRadius: 6,
          // Курс Є → блакитний (інформація); НЕМАЄ → жовтий (потрібна дія:
          // без курсу собівартість партій піде у ВАЛЮТІ, 2026-08-09).
          background: fxRate ? '#eff6ff' : '#fef3c7',
          border: `1px solid ${fxRate ? '#bfdbfe' : '#f59e0b'}`,
          color: fxRate ? '#1e40af' : '#b45309' }}>
          <span>
            Курс {docQuery.data.currency_code} *:{' '}
            <b>{fxRate
              ? Number(fxRate).toLocaleString('uk-UA', { maximumFractionDigits: 4 })
              : '— не вказано'}</b>
          </span>
          {!fxRate && (
            <span style={{ fontSize: '0.82rem' }}>
              обовʼязковий: без курсу собівартість партій піде у валюті
            </span>
          )}
          {fxRate && (
            <span>
              Разом у грн: <b>{fmt(totalAmount * Number(fxRate))}</b>
            </span>
          )}
          {!docQuery.data.external_source && !isPosted && (
            <button type="button" style={{ padding: '0.2rem 0.6rem', fontSize: '0.82rem' }}
              onClick={() => { setNbuDate(form.document_date.slice(0, 10)); setNbuOpen(true); }}
              title="Отримати офіційний курс НБУ на дату">
              отримати курс НБУ
            </button>
          )}
          {docQuery.data.external_source && (
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
              (курс з 1С)
            </span>
          )}
        </div>
      )}

      {nbuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
          background: 'var(--overlay)', display: 'flex',
          alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setNbuOpen(false)}>
          <div className="card" style={{ width: 420, margin: 0 }}
            onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Курс НБУ</h3>
              <button style={{ marginLeft: 'auto' }}
                onClick={() => setNbuOpen(false)}>закрити</button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
              Офіційний курс {docQuery.data?.currency_code} на дату
              (bank.gov.ua). У вихідні/свята курсу немає — візьміть
              найближчий робочий день.
            </p>
            <div className="field">
              <label>Дата курсу</label>
              <DatePopover value={nbuDate} onChange={setNbuDate} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end',
              gap: '0.5rem', marginTop: '0.75rem' }}>
              <button onClick={() => setNbuOpen(false)}>скасувати</button>
              <button className="primary" disabled={!nbuDate || nbuBusy}
                onClick={async () => {
                  const code = docQuery.data?.currency_code;
                  if (!code || !id) return;
                  setNbuBusy(true); setError(null);
                  try {
                    const res = await currencyRate.get(code, nbuDate);
                    await goodsReceipts.update(id, { exchange_rate: res.rate });
                    setFxRate(res.rate);
                    qc.invalidateQueries({ queryKey: ['goods-receipt', id] });
                    setNbuOpen(false);
                  } catch (e: any) {
                    setError(e?.response?.data?.detail ?? e?.message ?? String(e));
                  } finally {
                    setNbuBusy(false);
                  }
                }}>
                {nbuBusy ? 'отримання…' : 'отримати'}
              </button>
            </div>
          </div>
        </div>
      )}

      </>)}

      {/* ТЧ «Послуги» (2026-07-29): Дт рахунок витрат рядка — Кт 631. */}
      {viewTab === 'lines' && linesTab === 'services' && (
        <>
          {form.services.length > 0 && (
            <TchTable>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ minWidth: 170 }}>Номенклатура (послуга)</th>
                  <th style={{ minWidth: 150 }}>Зміст</th>
                  <th className="num" style={{ width: 80 }}>К-сть</th>
                  <th className="num" style={{ width: 100 }}>{form.vat_flags.prices_include_vat ? 'Ціна з ПДВ' : 'Ціна'}</th>
                  <th className="num" style={{ width: 110 }}>{showVatColumns ? 'Сума без ПДВ' : 'Сума'}</th>
                  {/* ч.751: гривневий еквівалент — як на вкладках товарів/обладнання. */}
                  {isFxDoc && (
                    <th className="num" style={{ width: 130 }}>За курсом НБУ, грн</th>
                  )}
                  {showVatColumns && (
                    <>
                      <th style={{ width: 100 }}>Ставка</th>
                      <th className="num" style={{ width: 100 }}>ПДВ</th>
                      <th className="num" style={{ width: 110 }}>Усього</th>
                    </>
                  )}
                  {/* 2026-07-31: «Рахунок витрат» переїхав на вкладку
                      «Рахунки обліку» (зауваження власника — дубль). */}
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.services.map((sv, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <NomenclatureSelect
                        value={sv.nomenclature_id ?? ''}
                        kind="services"
                        disabled={isPosted}
                        onChange={(nid) => {
                          const nm = nomList.data?.items.find((n) => n.id === nid);
                          setService(i, {
                            nomenclature_id: nid || null,
                            description: (sv.description ?? '') || (nm?.name ?? ''),
                            // Стаття затрат — авто з картки (якщо у рядку порожньо).
                            cost_item_id: sv.cost_item_id
                              || nm?.default_cost_item_id || null,
                          });
                        }} />
                    </td>
                    <td>
                      <input value={sv.description ?? ''} disabled={isPosted}
                        onChange={(e) => setService(i, { description: e.target.value })} />
                    </td>
                    <td>
                      <input type="number" step="0.001" min={0} value={sv.quantity ?? ''}
                        disabled={isPosted} style={{ width: '100%' }}
                        onChange={(e) => setService(i, { quantity: e.target.value })} />
                    </td>
                    <td>
                      <input type="number" step="0.01" min={0} value={sv.price ?? ''}
                        disabled={isPosted} style={{ width: '100%' }}
                        onChange={(e) => setService(i, { price: e.target.value })} />
                    </td>
                    <td>
                      <input type="number" step="0.01" min={0} value={sv.amount_without_vat}
                        disabled={isPosted} style={{ width: '100%', textAlign: 'right' }}
                        onChange={(e) => setService(i, { amount_without_vat: e.target.value })} />
                    </td>
                    {isFxDoc && (
                      <td style={{ textAlign: 'right', color: fxNum ? '#1e40af' : 'var(--muted)', fontWeight: 600 }}>
                        {fxNum ? fmt(Number(sv.amount_without_vat || 0) * fxNum) : '—'}
                      </td>
                    )}
                    {showVatColumns && (
                      <>
                        <td>
                          <select value={sv.vat_rate_id ?? ''} disabled={isPosted}
                            onChange={(e) => setService(i, { vat_rate_id: e.target.value || null })}>
                            <option value="">—</option>
                            {vatList.data?.map((v) => (
                              <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {!form.vat_flags.vat_auto && !isPosted ? (
                            <input type="number" step="0.01" min="0" value={sv.vat_amount ?? '0'}
                                   title="Сума ПДВ вручну (авторозрахунок ПДВ вимкнено)"
                                   onChange={(e) => setService(i, { vat_amount: e.target.value })} />
                          ) : fmt(Number(sv.vat_amount || 0))}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {fmt(Number(sv.amount_without_vat || 0) + Number(sv.vat_amount || 0))}
                        </td>
                      </>
                    )}
                    <td>
                      {!isPosted && (
                        <button onClick={() => removeServiceRow(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 500 }}>
                  <td colSpan={5} style={{ textAlign: 'right' }}>Разом:</td>
                  <td style={{ textAlign: 'right' }}>{fmt(svcWithoutVat)}</td>
                  {isFxDoc && (
                    <td style={{ textAlign: 'right', color: 'var(--badge-blue-fg)' }}>
                      {fxNum ? fmt(svcWithoutVat * fxNum) : '—'}
                    </td>
                  )}
                  {showVatColumns && (
                    <>
                      <td></td>
                      <td style={{ textAlign: 'right' }}>{fmt(svcVat)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(svcWithoutVat + svcVat)}</td>
                    </>
                  )}
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right' }}>{t('gr.total')}:</td>
                  <td style={{ textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
                  {isFxDoc && (
                    <td style={{ textAlign: 'right', color: 'var(--badge-blue-fg)' }}>
                      {fxNum ? fmt(totalWithoutVat * fxNum) : '—'}
                    </td>
                  )}
                  {showVatColumns && (
                    <>
                      <td></td>
                      <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(totalAmount)}</td>
                    </>
                  )}
                  <td></td>
                </tr>
              </tfoot>
            </TchTable>
          )}
          {!isPosted && (
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.5rem' }}>
              проводка: Дт «рахунок витрат» — Кт 631 (без складського руху)
            </small>
          )}
        </>
      )}
      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (0419): скани накладної/фото — перший документ фічі. */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.goods_receipts" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error">{error}</div>}
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            // ч.914: створена ПОСЛУГА → рядок на вкладці «Послуги».
            if (nom.is_service || linesTab === 'services') {
              setForm((f) => ({
                ...f,
                services: [...f.services, {
                  line_number: f.services.length + 1,
                  nomenclature_id: nom.id, description: nom.name, quantity: '1', price: '',
                  amount_without_vat: '0',
                  vat_rate_id: showVatColumns
                    ? (nom.vat_rate_id ?? effectiveDefaultVatId ?? null) : (exemptVatId || null),
                  vat_amount: '0', total_amount: '0', cost_account_code: '92',
                  tax_purpose_id: nom.tax_purpose_id ?? defaultTaxPurposeId,
                }],
              }));
              setLinesTab('services'); setViewTab('lines');
              return;
            }
            setForm((f) => ({
              ...f,
              items: [...f.items, {
                ...newRow(f.items.length + 1,
                  nom.vat_rate_id ?? effectiveDefaultVatId, nom.tax_purpose_id ?? defaultTaxPurposeId),
                nomenclature_id: nom.id,
                account_code: linesTab === 'equipment' ? '152' : null,
              }],
            }));
          }}
        />
      )}
      {quickFa && form.organization_id && (
        <QuickFixedAssetModal
          organizationId={form.organization_id}
          defaultName={quickFa.name}
          defaultCost={quickFa.cost}
          onClose={() => setQuickFa(null)}
        />
      )}
      {invoicePickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setInvoicePickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>
                Активні рахунки постачальника{form.contractor_id ? '' : ' (усі)'}
              </h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setInvoicePickerOpen(false)}>закрити</button>
            </div>
            {supplierInvoicesQ.isLoading && <p>Завантаження…</p>}
            {!supplierInvoicesQ.isLoading && (supplierInvoicesQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>
                Немає відкритих проведених рахунків постачальників.
              </p>
            )}
            <table className="tch">
              <tbody>
                {supplierInvoicesQ.data?.items.map((o) => (
                  <tr key={o.id} onClick={() => void applyFromInvoice(o.id)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{o.document_number}</td>
                    <td>
                      {new Date(o.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {fmt(Number(o.total_amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          onPick={addRowFromPicker}
          onPickMany={(rows) => {
            if (linesTab === 'services') addServiceRows(rows.map((r) => r.nomenclature_id));
            else rows.forEach(addRowFromPicker);
            setPickerOpen(false);
          }}
          multiSelect={linesTab === 'services'}
          onlyKind={linesTab === 'services' ? 'services' : 'goods'}
          onClose={() => setPickerOpen(false)}
          catalogMode
        />
      )}
      {/* ч.948: попапи швидкого створення просто з форми документа. */}
      {quickContractor && (
        <QuickContractorModal
          direction={'OUT'}
          onClose={() => setQuickContractor(false)}
          onCreated={(cid) => {
            setQuickContractor(false);
            setForm((f) => ({ ...f, contractor_id: cid, contract_id: '' }));
            qc.invalidateQueries({ queryKey: ['contractors'] });
          }} />
      )}
      {quickContract && form.contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.contractor_id}
          contractorName={contractorNameForQuick}
          direction={'OUT'}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.contractor_id] });
          }} />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
